import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';

// SVG Icons
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const FileTextIcon = ({ size = 48 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);
const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const SparklesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3L14 10L21 12L14 14L12 21L10 14L3 12L10 10L12 3Z"></path>
  </svg>
);
const AlertTriangleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

interface InsegnaCliente {
  id: number;
  nome: string;
}

interface Allegato {
  id: number;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  uploadedAt: string;
}

interface Contratto {
  id: number;
  societaId: number;
  insegnaClienteId: number;
  anno: number;
  descrizione: string;
  riepilogoAI: string | null;
  riepilogoAIGeneratoAt: string | null;
  riepilogoAIEditato: boolean;
  note: string | null;
  allegati: Allegato[];
  updatedAt: string;
}

interface ModalData {
  id?: number;
  descrizione: string;
  note: string;
  riepilogoAI: string;
  riepilogoAIGeneratoAt: string | null;
  riepilogoAIEditato: boolean;
  allegatiEsistenti: Allegato[];
  nuoviFile: File[];
}

export default function ContrattiInsegnePage() {
  const annoCorrente = new Date().getFullYear();
  // Arrivando dalla scheda insegna: ?insegna=ID&anno=AAAA preseleziona i filtri
  const [searchParams] = useSearchParams();
  const insegnaDaUrl = Number(searchParams.get('insegna')) || null;
  const annoDaUrl = Number(searchParams.get('anno')) || null;
  const anniDisponibili = Array.from({ length: 6 }, (_, i) => annoCorrente - i);
  if (annoDaUrl && !anniDisponibili.includes(annoDaUrl)) {
    anniDisponibili.push(annoDaUrl);
    anniDisponibili.sort((a, b) => b - a);
  }

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Stato per bloccare societaId all'apertura della modale
  const [modalSocietaId, setModalSocietaId] = useState<string>('');

  const [insegne, setInsegne] = useState<InsegnaCliente[]>([]);
  const [insegnaSelezionata, setInsegnaSelezionata] = useState<number | null>(insegnaDaUrl);
  const [annoSelezionato, setAnnoSelezionato] = useState<number>(annoDaUrl || annoCorrente);

  const [contratti, setContratti] = useState<Contratto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>({
    descrizione: '',
    note: '',
    riepilogoAI: '',
    riepilogoAIGeneratoAt: null,
    riepilogoAIEditato: false,
    allegatiEsistenti: [],
    nuoviFile: [],
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carica insegne
  useEffect(() => {
    const loadInsegne = async () => {
      if (!pronta) return;
      try {
        const p = new URLSearchParams();
        if (filterSocietaId) p.append('societaId', filterSocietaId);
        const data = await apiFetch<InsegnaCliente[]>(`/api/anagrafiche/insegne?${p}`);
        setInsegne(data);
        // Insegna non (più) in elenco, es. dopo un cambio società: si riparte dalla prima
        if (data.length > 0 && !data.some((i) => i.id === insegnaSelezionata)) {
          setInsegnaSelezionata(data[0].id);
        }
      } catch (err) {
        console.error('Errore caricamento insegne:', err);
      }
    };
    loadInsegne();
  }, [pronta, filterSocietaId]);

  // Carica contratti quando cambiano insegna o anno
  const loadContratti = useCallback(async () => {
    if (!insegnaSelezionata || !pronta) return;

    try {
      setLoading(true);
      const p = new URLSearchParams();
      p.append('insegnaClienteId', insegnaSelezionata.toString());
      p.append('anno', annoSelezionato.toString());
      if (filterSocietaId) p.append('societaId', filterSocietaId);

      const data = await apiFetch<Contratto[]>(`/api/anagrafiche/contratti-insegna?${p}`);
      setContratti(data);
    } catch (err) {
      console.error('Errore caricamento contratti:', err);
    } finally {
      setLoading(false);
    }
  }, [insegnaSelezionata, annoSelezionato, pronta, filterSocietaId]);

  useEffect(() => {
    loadContratti();
  }, [loadContratti]);

  const handleCreate = () => {
    // Blocca societaId all'apertura della modale
    setModalSocietaId(filterSocietaId);

    setModalData({
      descrizione: '',
      note: '',
      riepilogoAI: '',
      riepilogoAIGeneratoAt: null,
      riepilogoAIEditato: false,
      allegatiEsistenti: [],
      nuoviFile: [],
    });
    setError('');
    setShowModal(true);
  };

  const handleEdit = (contratto: Contratto) => {
    // Blocca societaId all'apertura della modale - usa la società del contratto
    setModalSocietaId(String(contratto.societaId));

    setModalData({
      id: contratto.id,
      descrizione: contratto.descrizione,
      note: contratto.note || '',
      riepilogoAI: contratto.riepilogoAI || '',
      riepilogoAIGeneratoAt: contratto.riepilogoAIGeneratoAt,
      riepilogoAIEditato: contratto.riepilogoAIEditato,
      allegatiEsistenti: contratto.allegati || [],
      nuoviFile: [],
    });
    setError('');
    setShowModal(true);
  };

  const handleDelete = async (contratto: Contratto) => {
    if (!window.confirm(`Confermi l'eliminazione del contratto "${contratto.descrizione}"?`)) {
      return;
    }

    try {
      await apiFetch(`/api/anagrafiche/contratti-insegna/${contratto.id}`, { method: 'DELETE' });
      loadContratti();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione del contratto');
    }
  };

  const handleSave = async () => {
    if (!modalData.descrizione.trim()) {
      setError('Descrizione obbligatoria');
      return;
    }

    if (!modalSocietaId) {
      setError('Società non selezionata');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        societaId: parseInt(modalSocietaId, 10),
        insegnaClienteId: insegnaSelezionata,
        anno: annoSelezionato,
        descrizione: modalData.descrizione.trim(),
        note: modalData.note.trim() || null,
      };

      let contrattoId: number;

      if (modalData.id) {
        // Modifica
        await apiFetch(`/api/anagrafiche/contratti-insegna/${modalData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        contrattoId = modalData.id;
      } else {
        // Creazione
        const nuovoContratto = await apiFetch<Contratto>('/api/anagrafiche/contratti-insegna', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        contrattoId = nuovoContratto.id;
      }

      // Upload nuovi file se presenti
      if (modalData.nuoviFile.length > 0) {
        const formData = new FormData();
        modalData.nuoviFile.forEach((file) => {
          formData.append('files', file);
        });

        await apiFetch(`/api/anagrafiche/contratti-insegna/${contrattoId}/allegati`, {
          method: 'POST',
          body: formData,
        });
      }

      setShowModal(false);
      loadContratti();
    } catch (err: any) {
      if (err.message?.includes('409') || err.message?.toLowerCase().includes('già esiste')) {
        setError('Esiste già un contratto per questa insegna e anno');
      } else {
        setError(err.message || 'Errore nel salvataggio del contratto');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSalvaRiepilogoAI = async () => {
    if (!modalData.id) {
      setError('Salva prima il contratto');
      return;
    }

    try {
      setSaving(true);
      setError('');

      await apiFetch(`/api/anagrafiche/contratti-insegna/${modalData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          societaId: parseInt(modalSocietaId, 10),
          descrizione: modalData.descrizione,
          note: modalData.note,
          riepilogoAI: modalData.riepilogoAI,
          riepilogoAIEditato: modalData.riepilogoAIEditato,
        }),
      });

      alert('Riepilogo salvato con successo');
      loadContratti();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio del riepilogo');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalizzaAI = async () => {
    if (!modalData.id) {
      setError('Salva prima il contratto');
      return;
    }

    const totaleAllegati = modalData.allegatiEsistenti.length + modalData.nuoviFile.length;
    if (totaleAllegati === 0) {
      setError('Carica almeno un PDF prima di analizzare');
      return;
    }

    // Controllo rigenerazione su riepilogo editato
    if (modalData.riepilogoAI && modalData.riepilogoAIEditato) {
      const conferma = window.confirm(
        'Il riepilogo è stato modificato manualmente. La rigenerazione sovrascriverà le tue modifiche. Continuare?'
      );
      if (!conferma) return;
    }

    try {
      setAnalyzingAI(true);
      setError('');

      const risultato = await apiFetch<{ riepilogo: string }>(
        `/api/anagrafiche/contratti-insegna/${modalData.id}/analizza-ai`,
        { method: 'POST' }
      );

      setModalData((prev) => ({
        ...prev,
        riepilogoAI: risultato.riepilogo,
        riepilogoAIGeneratoAt: new Date().toISOString(),
        riepilogoAIEditato: false,
      }));
    } catch (err: any) {
      setError(err.message || 'Errore nell\'analisi AI');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const filesArray = Array.from(files);
    const pdfFiles = filesArray.filter((f) => f.type === 'application/pdf');

    if (pdfFiles.length !== filesArray.length) {
      setError('Sono accettati solo file PDF');
      return;
    }

    setModalData((prev) => ({
      ...prev,
      nuoviFile: [...prev.nuoviFile, ...pdfFiles],
    }));
    setError('');
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleRimuoviNuovoFile = (index: number) => {
    setModalData((prev) => ({
      ...prev,
      nuoviFile: prev.nuoviFile.filter((_, i) => i !== index),
    }));
  };

  const handleEliminaAllegatoEsistente = async (allegatoId: number) => {
    if (!modalData.id) return;

    if (!window.confirm('Confermi l\'eliminazione di questo allegato?')) return;

    try {
      await apiFetch(
        `/api/anagrafiche/contratti-insegna/${modalData.id}/allegati/${allegatoId}`,
        { method: 'DELETE' }
      );

      setModalData((prev) => ({
        ...prev,
        allegatiEsistenti: prev.allegatiEsistenti.filter((a) => a.id !== allegatoId),
      }));
    } catch (err: any) {
      setError(err.message || 'Errore nell\'eliminazione dell\'allegato');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const allegatiModificatiDopoAnalisi =
    modalData.id &&
    modalData.riepilogoAIGeneratoAt &&
    modalData.allegatiEsistenti.some(
      (a) => new Date(a.uploadedAt) > new Date(modalData.riepilogoAIGeneratoAt!)
    );

  // Messaggio di cortesia se non c'è società selezionata
  if (!pronta) {
    return (
      <Layout>
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Contratti Insegna</h2>
            <p className="bop-text-muted">Gestione contratti quadro per insegne clienti</p>
          </div>
        </div>
        <div className="bop-card">
          <div className="bop-contratti-insegne__empty">
            <h3 className="bop-contratti-insegne__empty-title">Seleziona una società</h3>
            <p className="bop-contratti-insegne__empty-text">
              Seleziona una società dalla barra in alto per visualizzare i contratti insegna
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Contratti Insegna</h2>
          <p className="bop-text-muted">Gestione contratti quadro per insegne clienti</p>
        </div>
      </div>

      {/* Filtri */}
      <div className="bop-contratti-insegne__filter-bar">
        <div className="bop-contratti-insegne__filter-row">
          {!isUnica && (
            <div className="bop-contratti-insegne__filter-col--societa">
              <label className="bop-label">Società</label>
              <select
                value={filterSocietaId}
                onChange={(e) => setFilterSocietaId(e.target.value)}
                className="bop-select bop-contratti-insegne__select--auto"
              >
                <option value="">Tutte le società</option>
                {societaList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-contratti-insegne__filter-col--insegna">
            <label className="bop-label">Insegna</label>
            <select
              value={insegnaSelezionata || ''}
              onChange={(e) => setInsegnaSelezionata(parseInt(e.target.value))}
              className="bop-select"
            >
              {insegne.map((ins) => (
                <option key={ins.id} value={ins.id}>
                  {ins.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="bop-contratti-insegne__filter-col--anno">
            <label className="bop-label">Anno</label>
            <select
              value={annoSelezionato}
              onChange={(e) => setAnnoSelezionato(parseInt(e.target.value))}
              className="bop-select"
            >
              {anniDisponibili.map((anno) => (
                <option key={anno} value={anno}>
                  {anno}
                </option>
              ))}
            </select>
          </div>

          <div className="bop-contratti-insegne__filter-col--action">
            <button className="bop-btn-primary" onClick={handleCreate}>
              <PlusIcon />
              Aggiungi contratto
            </button>
          </div>
        </div>
      </div>

      {/* Lista contratti */}
      <div className="bop-card">
        {loading ? (
          <div className="bop-contratti-insegne__loading">
            <p className="bop-contratti-insegne__loading-text">Caricamento in corso...</p>
          </div>
        ) : contratti.length === 0 ? (
          <div className="bop-contratti-insegne__empty">
            <div className="bop-contratti-insegne__empty-icon">
              <FileTextIcon size={48} />
            </div>
            <h3 className="bop-contratti-insegne__empty-title">Nessun contratto configurato</h3>
            <p className="bop-contratti-insegne__empty-text">Inizia creando il primo contratto per questa insegna</p>
          </div>
        ) : (
          <div className="bop-table-wrapper">
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Anno</th>
                  <th>Descrizione</th>
                  <th>Allegati</th>
                  <th>Riepilogo AI</th>
                  <th className="bop-contratti-insegne__actions-col">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {contratti.map((contratto) => (
                  <tr key={contratto.id}>
                    <td>
                      <strong>{contratto.anno}</strong>
                    </td>
                    <td>{contratto.descrizione}</td>
                    <td>
                      <span
                        className={contratto.allegati?.length > 0
                          ? 'bop-contratti-insegne__badge--has-files'
                          : 'bop-contratti-insegne__badge--no-files'}
                      >
                        {contratto.allegati?.length || 0} PDF
                      </span>
                    </td>
                    <td>
                      <span
                        className={contratto.riepilogoAI
                          ? 'bop-contratti-insegne__badge--ai-present'
                          : 'bop-contratti-insegne__badge--ai-missing'}
                      >
                        {contratto.riepilogoAI ? 'Presente' : 'Non generato'}
                      </span>
                    </td>
                    <td>
                      <div className="bop-flex">
                        <button
                          className="bop-btn-icon"
                          onClick={() => handleEdit(contratto)}
                          title="Apri"
                        >
                          <FileTextIcon size={16} />
                        </button>
                        <button
                          className="bop-btn-icon"
                          onClick={() => handleDelete(contratto)}
                          title="Elimina"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modale Dettaglio Contratto */}
      {showModal && (
        <div className="bop-modal-overlay">
          <div
            className="bop-modal bop-modal--xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{modalData.id ? 'Modifica Contratto' : 'Nuovo Contratto'}</h3>
              <button className="bop-modal__close" onClick={() => setShowModal(false)}>
                <XIcon />
              </button>
            </div>

            <div className="bop-modal__body">
              {error && (
                <div className="bop-inline-error bop-contratti-insegne__alert">
                  {error}
                </div>
              )}

              {/* Società - visibile solo se non è unica */}
              {!isUnica && (
                <div className="bop-form-group bop-contratti-insegne__form-group">
                  <label className="bop-label">
                    Società <span className="bop-contratti-insegne__required">*</span>
                  </label>
                  <select
                    className="bop-select"
                    value={modalSocietaId}
                    disabled
                  >
                    <option value="">Seleziona società...</option>
                    {societaList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome}
                      </option>
                    ))}
                  </select>
                  <small className="bop-contratti-insegne__hint">
                    La società è stata bloccata all'apertura della modale
                  </small>
                </div>
              )}

              {/* ──────────────────────────────────────────────────── */}
              {/* SEZIONE: DATI CONTRATTO */}
              {/* ──────────────────────────────────────────────────── */}
              <div className="bop-contratti-insegne__section">
                <h4 className="bop-contratti-insegne__section-title">
                  Dati Contratto
                </h4>

                <div className="bop-form-group">
                  <label className="bop-label">
                    Descrizione <span className="bop-contratti-insegne__required">*</span>
                  </label>
                  <input
                    type="text"
                    className="bop-input"
                    value={modalData.descrizione}
                    onChange={(e) => setModalData({ ...modalData, descrizione: e.target.value })}
                    placeholder="Es. Contratto quadro 2024"
                  />
                </div>

                <div className="bop-form-group">
                  <label className="bop-label">Note</label>
                  <textarea
                    className="bop-input"
                    value={modalData.note}
                    onChange={(e) => setModalData({ ...modalData, note: e.target.value })}
                    rows={3}
                    placeholder="Note aggiuntive..."
                  />
                </div>

                {/* Area Upload Allegati */}
                <div className="bop-form-group">
                  <label className="bop-label">Allegati PDF</label>

                  {/* Drag&Drop Zone */}
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`bop-contratti-insegne__dropzone${dragActive ? ' bop-contratti-insegne__dropzone--active' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <UploadIcon />
                    <p className="bop-contratti-insegne__dropzone-text">
                      Trascina i file PDF qui o clicca per selezionare
                    </p>
                    <p className="bop-contratti-insegne__dropzone-hint">
                      Massimo 10 file PDF
                    </p>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    multiple
                    className="bop-contratti-insegne__file-input-hidden"
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />

                  {/* Lista allegati esistenti */}
                  {modalData.allegatiEsistenti.length > 0 && (
                    <div className="bop-contratti-insegne__file-list">
                      <h5 className="bop-contratti-insegne__file-list-title">
                        Allegati già caricati:
                      </h5>
                      {modalData.allegatiEsistenti.map((allegato) => (
                        <div
                          key={allegato.id}
                          className="bop-contratti-insegne__file-item bop-contratti-insegne__file-item--existing"
                        >
                          <div className="bop-contratti-insegne__file-info">
                            <span className="bop-contratti-insegne__file-name">
                              {allegato.fileName}
                            </span>
                            <span className="bop-contratti-insegne__file-size">
                              {formatFileSize(allegato.fileSize)}
                            </span>
                          </div>
                          <button
                            className="bop-btn-icon"
                            onClick={() => handleEliminaAllegatoEsistente(allegato.id)}
                            title="Elimina"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lista nuovi file */}
                  {modalData.nuoviFile.length > 0 && (
                    <div className="bop-contratti-insegne__file-list">
                      <h5 className="bop-contratti-insegne__file-list-title">
                        File da caricare:
                      </h5>
                      {modalData.nuoviFile.map((file, index) => (
                        <div
                          key={index}
                          className="bop-contratti-insegne__file-item bop-contratti-insegne__file-item--new"
                        >
                          <div className="bop-contratti-insegne__file-info">
                            <span className="bop-contratti-insegne__file-name">
                              {file.name}
                            </span>
                            <span className="bop-contratti-insegne__file-size">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                          <button
                            className="bop-btn-icon"
                            onClick={() => handleRimuoviNuovoFile(index)}
                            title="Rimuovi"
                          >
                            <XIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ──────────────────────────────────────────────────── */}
              {/* SEZIONE: RIEPILOGO AI */}
              {/* ──────────────────────────────────────────────────── */}
              <div className="bop-contratti-insegne__section bop-contratti-insegne__section--last">
                <h4 className="bop-contratti-insegne__section-title">
                  <SparklesIcon /> Riepilogo AI
                </h4>

                {/* Banner allegati modificati */}
                {allegatiModificatiDopoAnalisi && (
                  <div className="bop-contratti-insegne__ai-warning">
                    <AlertTriangleIcon />
                    <p className="bop-contratti-insegne__ai-warning-text">
                      Gli allegati sono stati modificati. Il riepilogo potrebbe non essere aggiornato.
                    </p>
                  </div>
                )}

                {!modalData.riepilogoAI ? (
                  <div className="bop-contratti-insegne__ai-empty">
                    <p className="bop-contratti-insegne__ai-empty-text">
                      Nessun riepilogo disponibile
                    </p>
                    <button
                      className="bop-btn-primary"
                      onClick={handleAnalizzaAI}
                      disabled={
                        analyzingAI ||
                        !modalData.id ||
                        modalData.allegatiEsistenti.length + modalData.nuoviFile.length === 0
                      }
                    >
                      {analyzingAI ? (
                        <>Analisi in corso... Gemini sta leggendo i documenti</>
                      ) : (
                        <>
                          <SparklesIcon /> Analizza con AI
                        </>
                      )}
                    </button>
                    {!modalData.id && (
                      <p className="bop-contratti-insegne__ai-save-hint">
                        Salva prima il contratto per abilitare l'analisi AI
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <textarea
                      className="bop-contratti-insegne__ai-textarea"
                      value={modalData.riepilogoAI}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          riepilogoAI: e.target.value,
                          riepilogoAIEditato: true,
                        })
                      }
                      rows={12}
                    />
                    <div className="bop-contratti-insegne__ai-actions">
                      <button
                        className="bop-btn-secondary"
                        onClick={handleAnalizzaAI}
                        disabled={analyzingAI}
                      >
                        {analyzingAI ? 'Analisi in corso...' : 'Rigenera con AI'}
                      </button>
                      <button className="bop-btn-primary" onClick={handleSalvaRiepilogoAI} disabled={saving}>
                        {saving ? 'Salvataggio...' : 'Salva riepilogo'}
                      </button>
                    </div>
                    {modalData.riepilogoAIEditato && (
                      <p className="bop-contratti-insegne__ai-edited-warning">
                        ⚠ Riepilogo modificato manualmente
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Chiudi
              </button>
              <button className="bop-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : modalData.id ? 'Salva modifiche' : 'Crea contratto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
